import { useCallback, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import FlipbookViewer, { FlipbookHandle, FlipbookHotspots, FlipbookSpreads } from '../components/FlipbookViewer';

const COVER_ASPECT_RATIO = 4266 / 5367;
const THICKNESS = 60;       // px — total book body thickness (pages)
const COVER_DEPTH = 20;     // px — hardcover board thickness
const PAGE_INSET = 4;       // px — cover overhang top/bottom (pages sit slightly inside)
const FORE_EDGE_INSET = 5;  // px — cover overhang on fore-edge (right)
const ROTATE_DURATION = 2.0; // seconds — hover turn speed (increase = slower)

// Spread images are pre-optimized by `frontend/scripts/optimize-spreads.ps1` into
// two tiers under each source folder:
//   full/Spread-N.jpg  — higher quality, shown once a page is at rest
//   thumb/Spread-N.jpg — small + fast, shown while a page is mid-flip
// FlipbookViewer derives the thumb URL from the full URL by convention, so only
// the `full` path needs to be listed here.

/** 14 full-bleed spreads: left-page keys 2, 4, …, 28 → Spread-1.jpg … Spread-14.jpg */
const MARINE_MORPH_SPREADS: FlipbookSpreads = Object.fromEntries(
  Array.from({ length: 14 }, (_, i) => [
    2 + i * 2,
    `/marine-morph/full/Spread-${i + 1}.jpg`,
  ]),
);

/** 14 full-bleed spreads: left-page keys 2, 4, …, 28 → Spread-1.jpg … Spread-14.jpg */
const SPECIES_HABITATS_SPREADS: FlipbookSpreads = Object.fromEntries(
  Array.from({ length: 14 }, (_, i) => [
    2 + i * 2,
    `/species-habitats/full/Spread-${i + 1}.jpg`,
  ]),
);

/** 12 full-bleed spreads: left-page keys 2, 4, …, 24 → Spread-1.jpg … Spread-12.jpg */
const BICAP_DEV_SPREADS: FlipbookSpreads = Object.fromEntries(
  Array.from({ length: 12 }, (_, i) => [
    2 + i * 2,
    `/BIOcap-Dev/full/Spread-${i + 1}.jpg`,
  ]),
);

// Spread 14 of Species & Habitats ("Tile Map") is a directory: each numbered
// tile on the elevation diagram links back to its own content spread.
// Tile N on the map -> Spread N+1 -> turn.js left-page key (2 * (N + 1)).
const SPECIES_HABITATS_TILE_MAP_HOTSPOTS: FlipbookHotspots = {
  28: [
    { side: 'left', xPct: 22.5, yPct: 42, wPct: 6, hPct: 7, targetPage: 4, sizeScale: 1.20, label: 'Go to Spread 2: Limpets' },
    { side: 'left', xPct: 35, yPct: 45, wPct: 6, hPct: 7, targetPage: 6, sizeScale: 1.18, label: 'Go to Spread 3: Barnacles' },
    { side: 'left', xPct: 47.2, yPct: 47.1, wPct: 6, hPct: 7.5, targetPage: 8, sizeScale: 1.15, label: 'Go to Spread 4: Crustose Coralline Algae' },
    { side: 'left', xPct: 64.4, yPct: 42, wPct: 6, hPct: 7, targetPage: 10, sizeScale: 1.30, label: 'Go to Spread 5: Mussels' },
    { side: 'left', xPct: 77, yPct: 47.5, wPct: 6, hPct: 7, targetPage: 12, sizeScale: 1.2, label: 'Go to Spread 6: Blennies' },
    { side: 'left', xPct: 88, yPct: 56.5, wPct: 6, hPct: 7, targetPage: 14, sizeScale: 1.10, oval: true, label: 'Go to Spread 7: Tube Sponges' },
    { side: 'right', xPct: 6.5, yPct: 56.5, wPct: 6, hPct: 7, targetPage: 16, sizeScale: 1.10, label: 'Go to Spread 8: Mud Crabs' },
    { side: 'right', xPct: 19, yPct: 39.5, wPct: 6, hPct: 7, targetPage: 18, sizeScale: 1.30, label: 'Go to Spread 9: Eastern Oysters' },
    { side: 'right', xPct: 31.7, yPct: 77.5, wPct: 6, hPct: 7, targetPage: 20, darkRing: true, sizeScale: 1.20, oval: true, label: 'Go to Spread 10: Sargeant Major' },
    { side: 'right', xPct: 48.2, yPct: 55.3, wPct: 6, hPct: 7, targetPage: 22, sizeScale: 1.25, label: 'Go to Spread 11: Yellow Porites' },
    { side: 'right', xPct: 61, yPct: 77.5, wPct: 6, hPct: 7, targetPage: 24, darkRing: true, sizeScale: 1.30, label: 'Go to Spread 12: Grey Snapper Fish' },
    { side: 'right', xPct: 73.6, yPct: 55, wPct: 6, hPct: 7, targetPage: 26, sizeScale: 1.35, label: 'Go to Spread 13' },
  ],
};

const books = [
  {
    id: 1,
    // Edge-averaged from EBook Covers-03.jpeg
    spine: '#318A94',
    image: '/book-covers/optimized/EBook Covers-03.jpeg',
    title: 'Marine Morphologies',
    backCoverTitle: 'Marine Morphologies',
    spreads: MARINE_MORPH_SPREADS,
    hotspots: {} as FlipbookHotspots,
  },
  {
    id: 2,
    // Edge-averaged from EBook Covers-02.jpg
    spine: '#779B92',
    image: '/book-covers/optimized/EBook Covers-02.jpg',
    title: 'Species & Habitats',
    backCoverTitle: 'Habitat Profiles',
    spreads: SPECIES_HABITATS_SPREADS,
    hotspots: SPECIES_HABITATS_TILE_MAP_HOTSPOTS,
  },
  {
    id: 3,
    // Edge-averaged from EBook Covers-01.jpeg
    spine: '#86A7A3',
    image: '/book-covers/optimized/EBook Covers-01.jpeg',
    title: 'BIOCAP Development',
    backCoverTitle: 'Biocap Development',
    spreads: BICAP_DEV_SPREADS,
    hotspots: {} as FlipbookHotspots,
  },
];

function Book({
  image,
  spineColor,
  backCoverTitle,
}: {
  image: string;
  spineColor: string;
  backCoverTitle: string;
}) {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      style={{
        perspective: '1400px',
        perspectiveOrigin: '50% 50%',
        width: '100%',
        height: '100%',
        filter: hovered
          ? 'drop-shadow(24px 24px 40px rgba(0,0,0,0.45))'
          : 'drop-shadow(10px 10px 20px rgba(0,0,0,0.28))',
        transition: `filter ${ROTATE_DURATION}s ease`,
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div
        style={{
          position: 'relative',
          width: '100%',
          height: '100%',
          transformStyle: 'preserve-3d',
          transform: hovered ? 'rotateY(-28deg)' : 'rotateY(0deg)',
          transition: `transform ${ROTATE_DURATION}s cubic-bezier(0.23, 1, 0.32, 1)`,
        }}
      >
        {/* Front cover — pushed forward to give board depth */}
        <img
          src={image}
          alt="Book cover"
          style={{
            display: 'block',
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            borderRadius: '1px 6px 6px 1px',
            transform: `translateZ(${COVER_DEPTH}px)`,
          }}
        />

        {/* Cover board — right edge */}
        <div
          style={{
            position: 'absolute',
            top: '6px',
            left: '100%',
            width: `${COVER_DEPTH}px`,
            height: 'calc(100% - 12px)',
            backgroundColor: spineColor,
            transformOrigin: 'left center',
            transform: `translateZ(${COVER_DEPTH}px) rotateY(90deg)`,
          }}
        />

        {/* Spine — left face with gradient */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: `-${THICKNESS}px`,
            width: `${THICKNESS}px`,
            height: '100%',
            background: `linear-gradient(
              to right,
              rgba(0,0,0,0.35) 0%,
              rgba(0,0,0,0.1) 18%,
              rgba(255,255,255,0.08) 45%,
              rgba(0,0,0,0.05) 75%,
              rgba(0,0,0,0.25) 100%
            ), ${spineColor}`,
            transformOrigin: 'right center',
            transform: 'rotateY(-90deg)',
            borderRadius: '4px 0 0 4px',
          }}
        />

        {/* Spine-to-cover shadow */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '18%',
            height: '100%',
            background: 'linear-gradient(to right, rgba(0,0,0,0.28) 0%, rgba(0,0,0,0) 100%)',
            borderRadius: '1px 0 0 1px',
            transform: `translateZ(${COVER_DEPTH + 0.1}px)`,
            pointerEvents: 'none',
          }}
        />

        {/* Hinge seam */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: '15px',
            width: '2px',
            height: '100%',
            background: 'rgba(0,0,0,0.45)',
            transform: `translateZ(${COVER_DEPTH + 0.2}px)`,
            pointerEvents: 'none',
          }}
        />

        {/* Hinge highlight */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: '17px',
            width: '1px',
            height: '100%',
            background: 'rgba(255,255,255,0.18)',
            transform: `translateZ(${COVER_DEPTH + 0.2}px)`,
            pointerEvents: 'none',
          }}
        />

        {/* Pages — fore-edge */}
        <div
          style={{
            position: 'absolute',
            top: `${PAGE_INSET}px`,
            left: `calc(100% - ${FORE_EDGE_INSET}px)`,
            width: `${THICKNESS}px`,
            height: `calc(100% - ${PAGE_INSET * 2}px)`,
            background:
              'repeating-linear-gradient(to right, #ccc9bb 0px, #e8e4d8 1px, #f2ede1 2px, #e0dbd0 3px, #ccc9bb 4px)',
            transformOrigin: 'left center',
            transform: `translateZ(${COVER_DEPTH * 0.5}px) rotateY(90deg)`,
          }}
        />

        {/* Back cover */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            backgroundColor: spineColor,
            transform: `translateZ(-${THICKNESS}px)`,
            borderRadius: '1px 6px 6px 1px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '8px',
          }}
        >
          <span
            style={{
              color: 'rgba(255,255,255,0.5)',
              fontSize: '10px',
              letterSpacing: '0.14em',
              fontFamily: 'sans-serif',
              textTransform: 'uppercase',
              textAlign: 'center',
              lineHeight: 1.35,
            }}
          >
            {backCoverTitle}
          </span>
        </div>
      </div>
    </div>
  );
}

function BookOverlay({
  bookIndex,
  visible,
  onClose,
}: {
  bookIndex: number;
  visible: boolean;
  onClose: () => void;
}) {
  const book = books[bookIndex];
  const flipbookRef = useRef<FlipbookHandle>(null);
  const pageInputRef = useRef<HTMLInputElement>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [editingPage, setEditingPage] = useState(false);
  const [pageDraft, setPageDraft] = useState('');

  const handlePageChange = useCallback((page: number, total: number) => {
    setCurrentPage(page);
    setTotalPages(total);
  }, []);

  const startEditingPage = useCallback(() => {
    setPageDraft(String(currentPage));
    setEditingPage(true);
  }, [currentPage]);

  const cancelEditingPage = useCallback(() => {
    setEditingPage(false);
    setPageDraft('');
  }, []);

  const commitPageJump = useCallback(() => {
    const parsed = Number.parseInt(pageDraft.trim(), 10);
    setEditingPage(false);
    setPageDraft('');
    if (!Number.isFinite(parsed)) return;
    const clamped = Math.min(Math.max(parsed, 1), totalPages);
    if (clamped === currentPage) return;
    flipbookRef.current?.goToPage(clamped);
  }, [pageDraft, totalPages, currentPage]);

  useEffect(() => {
    if (!editingPage) return;
    const input = pageInputRef.current;
    if (!input) return;
    input.focus();
    input.select();
  }, [editingPage]);

  useEffect(() => {
    if (!visible) {
      setEditingPage(false);
      setPageDraft('');
    }
  }, [visible]);

  useEffect(() => {
    if (!visible) return;

    const onKeyDown = (e: KeyboardEvent) => {
      // Don't steal keys while typing a page number (or any other field).
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || editingPage) return;

      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        flipbookRef.current?.prevPage();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        flipbookRef.current?.nextPage();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [visible, editingPage]);

  return (
    <div
      className={`fixed inset-0 flex flex-col ${
        visible ? 'z-50 visible' : 'z-[-1] invisible pointer-events-none'
      }`}
      style={{ backgroundColor: visible ? 'rgba(0, 0, 0, 0.85)' : 'transparent' }}
      aria-hidden={!visible}
      onClick={visible ? onClose : undefined}
    >
      {/* Top bar: book title · page indicator · close */}
      <div
        className="flex items-center justify-between px-8 py-4 shrink-0"
        onClick={(e) => e.stopPropagation()}
      >
        <span className="text-white text-sm font-medium tracking-wide opacity-80">
          {book.title}
        </span>
        <div className="text-white text-sm font-medium tracking-widest tabular-nums flex items-center gap-1">
          {editingPage ? (
            <input
              ref={pageInputRef}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={pageDraft}
              onChange={(e) => setPageDraft(e.target.value.replace(/[^\d]/g, ''))}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  commitPageJump();
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  cancelEditingPage();
                }
                e.stopPropagation();
              }}
              onBlur={cancelEditingPage}
              aria-label="Go to page"
              className="w-10 bg-white/10 border border-white/30 rounded px-1 py-0.5 text-center text-white text-sm font-medium tracking-widest tabular-nums outline-none focus:border-white/70"
            />
          ) : (
            <button
              type="button"
              onClick={startEditingPage}
              className="hover:bg-white/10 rounded px-1 py-0.5 transition-colors"
              aria-label="Jump to page"
              title="Click to jump to a page"
            >
              {currentPage}
            </button>
          )}
          <span className="opacity-80">/ {totalPages}</span>
        </div>
        <button
          onClick={onClose}
          className="text-white hover:text-gray-300 transition-colors"
          aria-label="Close"
        >
          <X className="w-6 h-6" />
        </button>
      </div>

      {/* Main area: arrow · flipbook · arrow */}
      <div
        className="flex flex-1 items-center justify-center gap-6 min-h-0 px-4 pb-6"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Previous-page arrow */}
        <button
          onClick={() => flipbookRef.current?.prevPage()}
          disabled={currentPage <= 1}
          className="text-white disabled:opacity-20 hover:text-gray-300 transition-colors shrink-0"
          aria-label="Previous page"
        >
          <ChevronLeft className="w-12 h-12" />
        </button>

        {/* Flipbook */}
        <FlipbookViewer
          ref={flipbookRef}
          cacheKey={String(book.id)}
          coverImage={book.image}
          spineColor={book.spine}
          backCoverTitle={book.backCoverTitle}
          spreads={book.spreads}
          spreadHotspots={book.hotspots}
          onPageChange={handlePageChange}
        />

        {/* Next-page arrow */}
        <button
          onClick={() => flipbookRef.current?.nextPage()}
          disabled={currentPage >= totalPages}
          className="text-white disabled:opacity-20 hover:text-gray-300 transition-colors shrink-0"
          aria-label="Next page"
        >
          <ChevronRight className="w-12 h-12" />
        </button>
      </div>
    </div>
  );
}

export default function Research() {
  const [openBookIndex, setOpenBookIndex] = useState<number | null>(null);
  const [mountedBooks, setMountedBooks] = useState<Set<number>>(() => new Set());

  const openBook = (index: number) => {
    setMountedBooks((prev) => new Set(prev).add(index));
    setOpenBookIndex(index);
  };

  const handleClose = () => setOpenBookIndex(null);

  return (
    <div className="bg-gradient-to-b from-[#E6F7F4] to-[#C5E8E3] min-h-[calc(100dvh-5rem)]">
      <Helmet>
        <title>Research | BIOCAP Website</title>
        <meta name="description" content="BIOCAP research initiatives and scientific work in coastal infrastructure and marine ecosystems." />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      </Helmet>

      <h1 className="text-4xl md:text-5xl font-bold text-center pt-10 pb-12">
        The Living Edge
      </h1>

      <div className="flex flex-wrap justify-center items-start gap-10 md:gap-14 px-6 md:px-16 pb-16 pt-4">
        {books.map((book, index) => (
          <div
            key={book.id}
            className="shrink-0 h-[calc(100dvh-448px)] max-h-[680px] cursor-pointer"
            style={{ aspectRatio: COVER_ASPECT_RATIO }}
            onClick={() => openBook(index)}
          >
            <Book image={book.image} spineColor={book.spine} backCoverTitle={book.backCoverTitle} />
          </div>
        ))}
      </div>

      {books.map((book, index) =>
        mountedBooks.has(index) ? (
          <BookOverlay
            key={book.id}
            bookIndex={index}
            visible={openBookIndex === index}
            onClose={handleClose}
          />
        ) : null,
      )}
    </div>
  );
}
